import { Debt } from '../../models/Entities/CashFlow/Debt/Debt';
import { Payment } from '../../models/Entities/CashFlow/Payment/Payment';
import { DebtPaymentImputation } from '../../models/Entities/CashFlow/Payment/DebtPaymentImputation';
import { currencyOf, isPending } from './General/Cashflow.Dashboard.Functions';

/**
 * Lógica de los formularios de deuda y pago, sin React: qué sugerir para no
 * pedir lo que ya se sabe, cómo repartir un pago entre deudas y qué validar.
 */

// ── Sugerencias ───────────────────────────────────────────────────────────

/**
 * Monedas para ofrecer como chips: primero las que ya se usaron con esa
 * contraparte, después las del resto de la caja. Siempre al menos una.
 */
export const suggestedCurrencies = (input: {
    counterpartId?: string;
    debts: Debt[];
    payments: Payment[];
    fallback?: string;
}): string[] => {
    const count = new Map<string, number>();
    const bump = (code: string, weight: number) => count.set(code, (count.get(code) ?? 0) + weight);

    const involves = (parties: string[]) => !input.counterpartId || parties.includes(input.counterpartId);

    input.debts.forEach((debt) => bump(currencyOf(debt), involves([debt.debtor, debt.creditor]) ? 1000 : 1));
    input.payments.forEach((payment) => bump(currencyOf(payment), involves([payment.payer, payment.receiver]) ? 1000 : 1));

    const ordered = Array.from(count.entries())
        .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
        .map(([code]) => code);

    if (ordered.length === 0) ordered.push(input.fallback || 'ARS');
    return ordered.slice(0, 4);
};

// ── Imputación ────────────────────────────────────────────────────────────

/** Importe de la imputación expresado en la moneda del pago. */
export const inPaymentCurrency = (imputation: DebtPaymentImputation): number =>
    imputation.amount * (imputation.exchangeRate || 1);

export interface ImputationTotals {
    /** Suma aplicada, en moneda del pago. */
    imputed: number;
    /** Lo que queda del pago sin aplicar (negativo si se pasó). */
    remaining: number;
    over: boolean;
}

const round2 = (value: number) => Math.round(value * 100) / 100;

export const imputationTotals = (imputations: DebtPaymentImputation[], paymentAmount: number): ImputationTotals => {
    const imputed = round2(imputations.reduce((acc, item) => acc + inPaymentCurrency(item), 0));
    const remaining = round2(paymentAmount - imputed);
    return { imputed, remaining, over: remaining < -0.005 };
};

/** Las deudas pendientes que este pago puede cancelar, de la más vieja a la más nueva. */
export const candidateDebts = (debts: Debt[], counterpartId: string | undefined, counterpartOf: (debt: Debt) => string): Debt[] =>
    debts
        .filter((debt) => isPending(debt) && (!counterpartId || counterpartOf(debt) === counterpartId))
        .sort((a, b) => new Date(a.debtDate).getTime() - new Date(b.debtDate).getTime());

/**
 * Reparte el pago entre las deudas, de la más vieja a la más nueva, hasta
 * agotarlo. Las deudas en otra moneda sólo entran si hay tipo de cambio para
 * esa moneda (unidades de la moneda del pago por unidad de la deuda).
 */
export const autoImpute = (input: {
    debts: Debt[];
    paymentAmount: number;
    paymentCurrency: string;
    rates?: Record<string, number>;
}): DebtPaymentImputation[] => {
    const result: DebtPaymentImputation[] = [];
    let remaining = round2(input.paymentAmount);

    for (const debt of input.debts) {
        if (remaining <= 0) break;
        const debtCurrency = currencyOf(debt);
        const rate = debtCurrency === input.paymentCurrency ? 1 : input.rates?.[debtCurrency];
        if (!rate || rate <= 0) continue;

        const pendingInPayment = round2((debt.pendingAmount ?? 0) * rate);
        const applyInPayment = Math.min(pendingInPayment, remaining);
        if (applyInPayment <= 0) continue;

        result.push({ debtId: debt.id, amount: round2(applyInPayment / rate), exchangeRate: rate });
        remaining = round2(remaining - applyInPayment);
    }

    return result;
};

/** Cambia (o agrega) la imputación de una deuda; con importe cero la saca. */
export const setImputation = (
    current: DebtPaymentImputation[],
    next: DebtPaymentImputation
): DebtPaymentImputation[] => {
    const others = current.filter((item) => item.debtId !== next.debtId);
    return next.amount > 0 ? [...others, next] : others;
};

// ── Validación ────────────────────────────────────────────────────────────

export interface DebtFormValues {
    counterpartId?: string;
    currency?: string;
    amount: number;
    date?: Date;
}

export const validateDebtForm = (values: DebtFormValues): Record<string, string> => {
    const errors: Record<string, string> = {};
    if (!values.counterpartId) errors.counterpart = 'Elegí con quién es la deuda.';
    if (!values.currency) errors.currency = 'Elegí la moneda.';
    if (!(values.amount > 0)) errors.amount = 'Ingresá un importe mayor a cero.';
    if (!values.date || Number.isNaN(new Date(values.date).getTime())) errors.date = 'Ingresá la fecha.';
    return errors;
};

export interface PaymentFormValues extends DebtFormValues {
    imputations: DebtPaymentImputation[];
}

export const validatePaymentForm = (values: PaymentFormValues): Record<string, string> => {
    const errors = validateDebtForm(values);
    if (values.amount > 0 && imputationTotals(values.imputations, values.amount).over) {
        errors.imputations = 'Lo aplicado a deudas supera el importe del pago.';
    }
    if (values.imputations.some((item) => !(item.exchangeRate > 0))) {
        errors.imputations = 'Falta el tipo de cambio de una deuda en otra moneda.';
    }
    return errors;
};

/** Texto numérico del usuario ("1.234,50", "1234.5") a número. */
export const parseAmount = (raw: string): number => {
    const cleaned = (raw || '').trim().replace(/\s/g, '');
    if (!cleaned) return 0;
    // Con coma y punto, el último separador es el decimal.
    const lastComma = cleaned.lastIndexOf(',');
    const lastDot = cleaned.lastIndexOf('.');
    let normalized = cleaned;
    if (lastComma > lastDot) normalized = cleaned.replace(/\./g, '').replace(',', '.');
    else normalized = cleaned.replace(/,/g, '');
    const value = parseFloat(normalized);
    return Number.isNaN(value) || value < 0 ? 0 : value;
};
